import FullCalendar, { type EventClickInfo, type EventInput } from "@fullcalendar/react";
import interactionPlugin from "@fullcalendar/react/interaction";
import timeGridPlugin from "@fullcalendar/react/timegrid";
import classicThemePlugin from "calendar/_views/full-calendar-theme";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import {
  calendarEventTimeFormat,
  clickableCalendarEventClass,
  clickableCalendarEventStyles,
  handleCalendarEventDidMount,
  handleCalendarEventWillUnmount,
} from "calendar/_views/calendar-event-presentation";
import type { CalendarEventOccurrenceTime } from "calendar/_schemas/calendar-event-schema";

type CalendarDayViewProps = {
  date: string;
  events: EventInput[];
  onEventClick: (eventId: string, occurrence: CalendarEventOccurrenceTime) => void;
  onNewEvent: () => void;
  canCreateEvent: boolean;
};

const DayViewLayout = styled(Box)(({ theme }) => ({
  height: "calc(100vh - 80px)",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  [theme.breakpoints.up("sm")]: { height: "70vh", minHeight: 520 },
}));
const DayViewActions = styled(Box)({ display: "flex", justifyContent: "flex-end" });
const DayCalendar = styled(Box)({ minHeight: 0, flex: 1, ...clickableCalendarEventStyles });

export default function CalendarDayView({ date, events, onEventClick, onNewEvent, canCreateEvent }: Readonly<CalendarDayViewProps>) {
  const handleEventClick = ({ event, jsEvent }: EventClickInfo) => {
    jsEvent.preventDefault();
    const eventId = event.extendedProps.calendarEventId;
    if (typeof eventId === "string") {
      onEventClick(eventId, {
        start: event.startStr,
        end: event.endStr || null,
        isAllDay: event.allDay,
        timeZoneId: null,
      });
    }
  };

  return (
    <DayViewLayout>
      <DayViewActions>
        <Button disabled={!canCreateEvent} variant="contained" onClick={onNewEvent}>New event</Button>
      </DayViewActions>
      <DayCalendar>
        <FullCalendar
          allDaySlot
          events={events}
          eventClass={clickableCalendarEventClass}
          eventClick={handleEventClick}
          eventTimeFormat={calendarEventTimeFormat}
          eventDidMount={handleCalendarEventDidMount}
          eventWillUnmount={handleCalendarEventWillUnmount}
          headerToolbar={false}
          height="100%"
          initialDate={date}
          initialView="timeGridDay"
          nowIndicator
          plugins={[timeGridPlugin, interactionPlugin, classicThemePlugin]}
          timeZone="local"
        />
      </DayCalendar>
    </DayViewLayout>
  );
}
